import React, { useRef, useState, useEffect } from 'react';

interface OTPInputProps {
  length?: number;
  onComplete?: (otp: string) => void;
  onChange?: (otp: string) => void;
  error?: boolean;
  success?: boolean;
  value?: string[]; // Controlled capability
  readOnly?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  onComplete,
  onChange,
  error,
  success,
  value: propValue,
  readOnly = false
}) => {
  const [otp, setOtp] = useState<string[]>(propValue || new Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (propValue) setOtp(propValue);
  }, [propValue]);

  const handleChange = (index: number, value: string) => {
    if (readOnly) return;
    if (isNaN(Number(value))) return;

    const newOtp = [...otp];
    // Allow only last character if multiple typed (though maxlength handles this usually)
    // If value is empty (backspace), substring(0) returns empty string, which is correct
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    const combinedOtp = newOtp.join('');
    if (onChange) {
      onChange(combinedOtp);
    }

    // Trigger complete
    if (combinedOtp.length === length && onComplete) {
      onComplete(combinedOtp);
    }

    // Move to next input if value exists
    if (value && index < length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (readOnly) return;
    if (e.key === 'Backspace' && !otp[index] && index > 0 && inputRefs.current[index - 1]) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (readOnly) return;
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, length).split('');
    if (pastedData.every(char => !isNaN(Number(char)))) {
        const newOtp = [...otp];
        pastedData.forEach((val, i) => {
            if (i < length) newOtp[i] = val;
        });
        setOtp(newOtp);

        const combinedOtp = newOtp.join('');
        if (onChange) onChange(combinedOtp);
        if (combinedOtp.length === length && onComplete) {
            onComplete(combinedOtp);
        }
    }
  };

  // Determine styles based on state
  const getBorderColor = (isActive: boolean) => {
    if (error) return 'border-error ring-1 ring-error/20 bg-error/5 text-error';
    if (success) return 'border-success text-success bg-success/5 font-bold';
    if (isActive) return 'border-primary ring-1 ring-primary/30';
    return 'border-slate-200 bg-white';
  };

  return (
    <div className={`flex justify-between gap-2 ${error ? 'animate-shake' : ''}`}>
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          maxLength={1}
          value={digit}
          readOnly={readOnly}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={`
            w-full h-12 text-center text-lg rounded-xl border outline-none transition-all
            font-semibold caret-primary
            ${getBorderColor(false)}
            focus:${!error && !success ? 'border-primary focus:ring-1 focus:ring-primary' : ''}
            ${readOnly ? 'cursor-default' : ''}
          `}
        />
      ))}
    </div>
  );
};
